import { useState, useEffect, useCallback, useRef } from "react";

import axios from "axios";
import { io, Socket } from "socket.io-client";

import { orgSessionManager } from "@domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "@platform/constants/shared-constants";

import { useStackRealtime } from "./useStackRealtime";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface GitOpsStatus {
  syncStatus: "Synced" | "OutOfSync" | "Unknown";
  healthStatus: string;
  revision: string;
  lastSyncAt?: string;
  commitSHA?: string;
  argocdAppName?: string;
}

export interface UseGitOpsStatusResult {
  gitopsStatus: GitOpsStatus | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const POLL_INTERVAL_MS = 30_000;

/**
 * Hook to fetch GitOps (ArgoCD) sync status for a system.
 *
 * Uses two update mechanisms:
 * 1. REST polling every 30 seconds (fallback)
 * 2. WebSocket real-time push via `gitops_status` event from the gateway
 *
 * The WebSocket event arrives when GitOpsStatusSyncService detects
 * a status change from ArgoCD (sync/health/drift).
 */
export function useGitOpsStatus(
  alias: string | undefined,
): UseGitOpsStatusResult {
  // ── Context consumption ──────────────────────────────────────────────────
  const ctx = useStackRealtime();

  // ── Fallback: always declare hooks to satisfy rules-of-hooks ──────────
  const [gitopsStatus, setGitOpsStatus] = useState<GitOpsStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);

  const token = () => orgSessionManager.getToken() ?? "";

  const fetchGitOpsStatus = useCallback(async () => {
    if (!alias) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get<GitOpsStatus | null>(
        `${apiBaseUrls.BACKEND}/api/v1/applications/${alias}/gitops-status`,
        { headers: { Authorization: `Bearer ${token()}` } },
      );
      setGitOpsStatus(res.data);
    } catch {
      setError("Failed to load GitOps status");
      setGitOpsStatus(null);
    } finally {
      setLoading(false);
    }
  }, [alias]);

  // REST polling fallback
  useEffect(() => {
    void fetchGitOpsStatus();

    const interval = setInterval(() => {
      void fetchGitOpsStatus();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [fetchGitOpsStatus]);

  // WebSocket subscription for real-time GitOps status updates
  useEffect(() => {
    if (!alias) return;

    const authToken = token();
    if (!authToken) return;

    const wsUrl = apiBaseUrls.WS ?? apiBaseUrls.BACKEND;
    const socket = io(`${wsUrl}/deployment-pipeline-events`, {
      auth: { token: authToken },
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 3000,
    });

    socket.on("connect", () => {
      socket.emit("subscribe", { systemAlias: alias });
    });

    socket.on(
      "gitops_status",
      (payload: {
        newStatus: string;
        healthStatus: string;
        revision: string;
      }) => {
        setGitOpsStatus((prev) =>
          prev
            ? {
                ...prev,
                syncStatus: payload.newStatus as GitOpsStatus["syncStatus"],
                healthStatus: payload.healthStatus,
                revision: payload.revision,
                lastSyncAt: new Date().toISOString(),
              }
            : prev,
        );
      },
    );

    socketRef.current = socket;

    return () => {
      socket.emit("unsubscribe", { systemAlias: alias });
      socket.disconnect();
      socketRef.current = null;
    };
  }, [alias]);

  // ── Context short-circuit: return shared data if available ──────────────
  if (ctx) {
    return {
      gitopsStatus: ctx.gitopsStatus,
      loading: ctx.gitopsLoading,
      error: ctx.gitopsError,
      refetch: ctx.refetchGitOps,
    };
  }

  return { gitopsStatus, loading, error, refetch: fetchGitOpsStatus };
}
